import React, { useMemo, useState } from 'react';
import { Drawer, Tag, Empty, Tooltip } from 'antd';
import {
    CheckCircleFilled,
    CloseCircleFilled,
    ExclamationCircleFilled,
    InfoCircleFilled,
    UpOutlined,
    DownOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { CellAlert } from '@liveline/ll_node_common/lib/models/websocket';
import useCellAlerts, { isAlertVisible, isAlertRoleBlocked } from '../../../hooks/useCellAlerts';
import ConfigProviderComponent from '../config-provider/ConfigProviderComponent';
import { antdTheme } from '../antdTheme';
import './alertFooterBar.scss';

const { CheckableTag } = Tag;

const SEVERITY_ICON: Record<string, React.ReactNode> = {
    critical: <CloseCircleFilled style={{ color: '#f5222d' }} />,
    warning: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
    info: <InfoCircleFilled style={{ color: '#006b8c' }} />,
};

// LT-3110 — a CLEARED message is a recovery, so it must not wear the severity
// glyph of the condition it retires: a producer whose clear text reads "part is
// present" rendered behind an amber warning triangle reads as a fresh fault
// (green matches the row's CLEARED state label).
const CLEARED_ICON: React.ReactNode = <CheckCircleFilled style={{ color: '#52c41a' }} />;

const alertIcon = (alert: CellAlert): React.ReactNode =>
    alert.state === 'cleared' ? CLEARED_ICON : SEVERITY_ICON[alert.severity] ?? SEVERITY_ICON.info;

/* LT-3193 — alerts outlive a shift, so a bare time-of-day can't tell yesterday's
   alert from this morning's. Month/day only: the feed is a live ring buffer, so
   nothing in it is old enough for the year to disambiguate anything. */
const formatTime = (ms: number): string => {
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '—';
    const date = d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
    return `${date}, ${d.toLocaleTimeString()}`;
};

/**
 * LT-3052 — the console "alarm line": a slim, always-present bar docked at the
 * bottom of the content area (mounted once in the private-routes layout) showing
 * severity counters + the most recent unresolved alert, expanding upward into a
 * bottom Drawer with the full live feed and audience filter chips.
 */
const AlertFooterBar: React.FC = () => {
    const { t } = useTranslation();
    const { alerts, facilityId, visibleAudiences, cellNames } = useCellAlerts();
    const [expanded, setExpanded] = useState(false);
    const [audienceFilter, setAudienceFilter] = useState<string[] | null>(null); // null = defaults
    // Opening (or closing) the drawer marks everything currently received as SEEN;
    // the bar's count chips then record only alerts arriving after that.
    const [seenTs, setSeenTs] = useState<Record<number, number>>({});
    const markSeen = () => setSeenTs((prev) => ({ ...prev, [facilityId]: Date.now() }));

    /**
     * Name the cell the way the rest of the console does ("Line 5"), falling
     * back to "Cell <id>" when the alert names a cell that is not in the
     * current facility's cell list (stale ring-buffer entry, facility switch
     * mid-flight, or a cell added since the list was cached).
     */
    const cellLabel = (id: number): string => cellNames[id] ?? `${t('cellAlerts.cell')} ${id}`;

    // Base visibility = hard role gate only (controls/internal need the role).
    // Unknown audiences pass through here so their chip can appear (default
    // off via activeFilter) — filtering fully by visibleAudiences at this
    // stage made unknown-audience alerts unreachable: no row AND no chip.
    const roleVisible = useMemo(
        () => alerts.filter((a) => !isAlertRoleBlocked(a, visibleAudiences)),
        [alerts, visibleAudiences],
    );

    // Audience chips = role-visible defaults + any extra values present in the data (open set)
    const audienceOptions = useMemo(() => {
        const present = new Set<string>(visibleAudiences);
        roleVisible.forEach((a) => (a.audience ?? []).forEach((aud) => present.add(aud)));
        return Array.from(present);
    }, [roleVisible, visibleAudiences]);

    const activeFilter = audienceFilter ?? visibleAudiences;
    const visible = useMemo(
        () =>
            roleVisible
                .filter((a) => isAlertVisible(a, activeFilter))
                // in-place cleared-folding keeps list positions — re-sort for display
                .sort((a, b) => b.time_stamp - a.time_stamp),
        [roleVisible, activeFilter],
    );

    const active = visible.filter((a) => a.state === 'raised');
    const activeCounts = {
        critical: active.filter((a) => a.severity === 'critical').length,
        warning: active.filter((a) => a.severity === 'warning').length,
        info: active.filter((a) => a.severity === 'info').length,
    };
    // Bar chips = NEW (unseen, non-cleared) since the drawer was last opened.
    // Compared on received_ms (browser arrival time) — producer time_stamps
    // can be clock-skewed and would mint alerts that arrive already "seen".
    // Reminders re-arrive, so a still-open condition re-counts after its mute
    // window — by design (it re-notified).
    const seen = seenTs[facilityId] ?? 0;
    const unseen = visible.filter((a) => a.state !== 'cleared' && a.received_ms > seen);
    const counts = {
        critical: unseen.filter((a) => a.severity === 'critical').length,
        warning: unseen.filter((a) => a.severity === 'warning').length,
        info: unseen.filter((a) => a.severity === 'info').length,
    };
    const latest: CellAlert | undefined = unseen[0] ?? active[0] ?? visible[0];
    // The bar stays visually hot while a condition is STILL RAISED, even if seen —
    // an ongoing fault must not go quiet because someone looked at it.
    const quiet = active.length === 0 && unseen.length === 0;

    const toggleAudience = (aud: string, checked: boolean) => {
        const next = checked ? [...activeFilter, aud] : activeFilter.filter((a) => a !== aud);
        setAudienceFilter(next);
    };

    if (!facilityId) {
        return <div className="alertFooterBar alertFooterBar--idle" data-testid="alert-footer-bar" />;
    }

    return (
        <ConfigProviderComponent theme={antdTheme}>
            <div
                className={`alertFooterBar ${quiet ? 'alertFooterBar--quiet' : 'alertFooterBar--active'}`}
                data-testid="alert-footer-bar"
                role="button"
                tabIndex={0}
                aria-label={t('cellAlerts.openFeed')}
                onClick={() => {
                    markSeen();
                    setExpanded(true);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        markSeen();
                        setExpanded(true);
                    }
                }}
            >
                <span className="alertFooterBar__counts">
                    {(['critical', 'warning', 'info'] as const).map((sev) => (
                        <Tooltip key={sev} title={t(`cellAlerts.tooltipNew_${sev}`)}>
                            <span
                                className={`alertFooterBar__count alertFooterBar__count--${sev} ${
                                    counts[sev] > 0 ? 'alertFooterBar__count--nonzero' : ''
                                }`}
                            >
                                {counts[sev]}
                            </span>
                        </Tooltip>
                    ))}
                </span>
                <span className="alertFooterBar__ticker">
                    {latest ? (
                        <>
                            {alertIcon(latest)}
                            <span
                                className={`alertFooterBar__tickerText ${
                                    latest.state === 'cleared' ? 'alertFooterBar__tickerText--cleared' : ''
                                }`}
                            >
                                {formatTime(latest.time_stamp)} · {cellLabel(latest.facility_line_id)} ·{' '}
                                {latest.code}
                                {/* the ticker has no state column, so a cleared
                                    condition must say so inline (LT-3110) */}
                                {latest.state === 'cleared' ? ` (${t('cellAlerts.stateCleared')})` : ''} ·{' '}
                                {latest.message}
                            </span>
                        </>
                    ) : (
                        <span className="alertFooterBar__tickerText alertFooterBar__tickerText--muted">
                            {t('cellAlerts.noAlerts')}
                        </span>
                    )}
                </span>
                <span className="alertFooterBar__copyright">
                    Copyright&#169; {new Date().getFullYear()} Liveline Technologies
                </span>
                <span className="alertFooterBar__expand">{expanded ? <DownOutlined /> : <UpOutlined />}</span>
            </div>

            <Drawer
                placement="bottom"
                height="45vh"
                open={expanded}
                onClose={() => {
                    markSeen(); // alerts that streamed in while open are seen too
                    setExpanded(false);
                }}
                className="alertFooterDrawer"
                title={
                    <div className="alertFooterDrawer__header">
                        <span>
                            {t('cellAlerts.title')}
                            <span className="alertFooterDrawer__activeSummary">
                                {(['critical', 'warning', 'info'] as const).map((sev) => (
                                    <Tooltip key={sev} title={t(`cellAlerts.tooltipActive_${sev}`)}>
                                        <span
                                            className={`alertFooterBar__count alertFooterBar__count--${sev} ${
                                                activeCounts[sev] > 0 ? 'alertFooterBar__count--nonzero' : ''
                                            }`}
                                        >
                                            {activeCounts[sev]}
                                        </span>
                                    </Tooltip>
                                ))}
                            </span>
                        </span>
                        <span className="alertFooterDrawer__chips">
                            {audienceOptions.map((aud) => (
                                <CheckableTag
                                    key={aud}
                                    checked={activeFilter.includes(aud)}
                                    onChange={(checked) => toggleAudience(aud, checked)}
                                >
                                    {aud}
                                </CheckableTag>
                            ))}
                        </span>
                    </div>
                }
            >
                {visible.length === 0 ? (
                    <Empty description={t('cellAlerts.noAlerts')} />
                ) : (
                    <div className="alertFooterDrawer__list">
                        {visible.map((a) => (
                            <div
                                key={a.alert_id}
                                className={`alertFooterDrawer__row alertFooterDrawer__row--${a.severity} alertFooterDrawer__row--state-${a.state}`}
                            >
                                <span className="alertFooterDrawer__icon">{alertIcon(a)}</span>
                                <span className="alertFooterDrawer__time">{formatTime(a.time_stamp)}</span>
                                <span className="alertFooterDrawer__cell">{cellLabel(a.facility_line_id)}</span>
                                <span className="alertFooterDrawer__code">{a.code}</span>
                                <span className="alertFooterDrawer__message">{a.message}</span>
                                {/* the badge owns a grid column of its own, so an
                                    unfolded row must still fill that cell — otherwise
                                    the state pill slides into it and wraps (LT-3193) */}
                                {(a.repeat_count ?? 1) > 1 ? (
                                    <Tooltip
                                        title={`${t('cellAlerts.repeatFolded')}: ${a.repeat_count} · ${formatTime(
                                            a.first_time_stamp ?? a.time_stamp,
                                        )} → ${formatTime(a.time_stamp)}`}
                                    >
                                        <span className="alertFooterDrawer__repeat">×{a.repeat_count}</span>
                                    </Tooltip>
                                ) : (
                                    <span aria-hidden="true" />
                                )}
                                <span className={`alertFooterDrawer__state alertFooterDrawer__state--${a.state}`}>
                                    {a.state === 'raised'
                                        ? t('cellAlerts.stateActive')
                                        : a.state === 'cleared'
                                        ? t('cellAlerts.stateCleared')
                                        : t('cellAlerts.stateEvent')}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </Drawer>
        </ConfigProviderComponent>
    );
};

export default AlertFooterBar;
